import { useEffect, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  ACTION_TYPE,
  CommonStepOne,
  CommonStepThree,
  CommonStepTwo,
  DIGITAL_TWIN_ACTION,
  QUERYPARAMS,
} from "../common";
import { StationSyncStatus, ViewCards } from "../components";
import useStationStatus from "./useStationStatus";
import BatteryNotDetectedDialog from "../components/ManualBatteryDialog";
import { useStepperContext } from "../StepperContext";
import { LoaderButton, CircularProgress } from "../../mui";
import useManualSwap from "../hooks/use-manual-swap";
import DoorPanel from "./DoorPanel";
import InBatterySelect from "../components/InBatterySelect";
import { useAuth } from "../../../contexts/AuthContext";

const DETECTION_TIMEOUT_MS = 20000;

const Section = ({ title, disabled = false, children }) => (
  <Stack
    gap={2}
    aria-disabled={disabled}
    sx={{
      opacity: disabled ? 0.5 : 1,
      pointerEvents: disabled ? "none" : "auto",
      transition: "opacity 0.2s",
    }}
  >
    <Typography variant="h6">{title}</Typography>
    {children}
  </Stack>
);

export default function DigitalTwinSwap() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, updateData, handleReset } = useStepperContext();
  const { isLoading, onSubmitManualSwap } = useManualSwap();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const userEmail = user?.email || "Unknown User";

  const reasonDone =
    data.swapReason === "Other" ? !!data.customReason : !!data.swapReason;
  const detailsDone = !!(
    data.partnerId &&
    data.driverId &&
    data.deviceId &&
    data.isDriverDetails
  );
  const swapStarted = !!data.batteryInsertionStartFromDigitalTwin;
  const batteryIdDone = !!data.insertedBatteryId;
  const inputsDone = reasonDone && detailsDone && batteryIdDone;
  const inDone = !!data.batteryInsertionCompleteFromDigitalTwin;
  const outDone = !!data.batteryOutCompleteFromDigitalTwin;
  const inUndetected = outDone && !data.insertedBatteryIdDT;

  const station = useStationStatus(detailsDone ? data.deviceId : null);

  useEffect(() => {
    updateData("swapType", QUERYPARAMS.DIGITAL_TWIN);
  }, []);

  useEffect(() => {
    if (!inUndetected) {
      setDialogOpen(false);
      return undefined;
    }
    const closedAt = data.batteryDoorClosedAt ?? Date.now();
    const remaining = Math.max(
      0,
      DETECTION_TIMEOUT_MS - (Date.now() - closedAt),
    );
    const timer = setTimeout(() => setDialogOpen(true), remaining);
    return () => clearTimeout(timer);
  }, [inUndetected]);

  const submitSwap = async () => {
    const payload = {
      deviceId: data.deviceId,
      partnerId: data.partnerId,
      driverId: data.driverId,
      manualSwapType: QUERYPARAMS.DIGITAL_TWIN,
      reasonForSwap: data.customReason || data.swapReason,
      insertedBatteryId: data.insertedBatteryIdDT || data.insertedBatteryId,
      insertedBatterySoc: data.insertedBatterySocDT || 0,
      insertedBatteryDoorCloseConf: data.insertedBatteryDoorCloseConf,
      batteryInsertedDetectionConf:
        data.batteryInsertedDetectionConf || ACTION_TYPE.MANUAL,
      batteryInsertedIntoDoor: data.batteryInsertedIntoDoor,
      takenBatteryId: data.takenBatteryIdDT,
      takenBatterySoc: data.takenBatterySocDT,
      batteryTakenFromDoor: data.batteryTakenFromDoor,
      takenBatteryDoorCloseConf: data.takenBatteryDoorCloseConf,
      swapInitiatedBy: userEmail,
    };

    try {
      await onSubmitManualSwap(payload);
      setSubmitted(true);
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const handleMainSubmit = () => {
    const currentId = data.insertedBatteryIdDT || data.insertedBatteryId;
    const isSameBattery =
      !!data.takenBatteryIdDT && data.takenBatteryIdDT === currentId;
    if (inUndetected || isSameBattery || !currentId) {
      setDialogOpen(true);
      return;
    }
    submitSwap();
  };

  const handleDialogSubmit = () => {
    setDialogOpen(false);
    submitSwap();
  };

  const handleSwapAgain = () => {
    setSubmitted(false);
    handleReset();
  };

  if (submitted) {
    return (
      <Stack gap={3}>
        <ViewCards view="all" />
        <Box>
          <Button variant="contained" onClick={handleSwapAgain}>
            Swap Again
          </Button>
        </Box>
      </Stack>
    );
  }

  const insertedId = data.insertedBatteryIdDT || data.insertedBatteryId;
  const sameBattery =
    !!data.takenBatteryIdDT && data.takenBatteryIdDT === insertedId;

  return (
    <Stack gap={4}>
      <Section title="Swap reason" disabled={swapStarted}>
        <CommonStepOne />
      </Section>

      <Section title="Partner and driver" disabled={!reasonDone || swapStarted}>
        <CommonStepTwo index={1} />
      </Section>

      <Section
        title="Battery given by driver"
        disabled={!reasonDone || !detailsDone || !!data.insertedBatteryIdDT}
      >
        <CommonStepThree index={2} />
        {data.insertedBatteryIdDT && (
          <Typography variant="body2" sx={{ color: "#079B27" }}>
            Auto detected in-battery: {data.insertedBatteryIdDT}
          </Typography>
        )}
      </Section>

      {(inputsDone || swapStarted) && (
        <Stack gap={4}>
          {!station.isReady ? (
            <CircularProgress />
          ) : (
            <>
              <StationSyncStatus
                syncTimestamp={station.syncTimestamp}
                isOnline={station.isOnline}
                stationIMEI={data.deviceId}
                partnerId={data.partnerId}
              />
              <DoorPanel
                action={DIGITAL_TWIN_ACTION.IN_BATTERY}
                station={station}
              />
              {inDone && (
                <DoorPanel
                  action={DIGITAL_TWIN_ACTION.OUT_BATTERY}
                  station={station}
                />
              )}
            </>
          )}

          {data.swapErrorRes && (
            <Typography variant="body2" color="error">
              {`${data.swapErrorRes.code}: ${data.swapErrorRes.message}`}
            </Typography>
          )}

          <Box>
            <LoaderButton
              variant="contained"
              onClick={handleMainSubmit}
              isLoading={isLoading}
              disabled={!outDone}
              sx={{ width: "auto", minWidth: "150px" }}
            >
              Submit
            </LoaderButton>
          </Box>
        </Stack>
      )}

      <BatteryNotDetectedDialog
        open={dialogOpen && (inUndetected || sameBattery || !insertedId)}
        doorNumber={Number(data.batteryInsertedIntoDoor) + 1}
        batteryId={data.insertedBatteryId}
        isDetected={!!data.insertedBatteryIdDT}
        batterySelect={<InBatterySelect />}
        submitDisabled={sameBattery || !data.insertedBatteryId}
        submitError={
          sameBattery ? "In battery and out battery cannot be the same." : ""
        }
        isLoading={isLoading}
        onWait={() => setDialogOpen(false)}
        onSubmit={handleDialogSubmit}
      />
    </Stack>
  );
}
